import { campaignApiGet, campaignApiPatch, campaignApiPost } from '@/lib/api/campaign-api-client';
import type { SettingsApiEnvelope } from '../types/compliance-settings.types';
import type {
  StartFromTemplateInput,
  StartFromTemplateResponse,
  SystemEmailDetail,
  SystemEmailPreview,
  SystemEmailPreviewInput,
  SystemEmailSummary,
  UpdateSystemEmailInput,
} from '../types/system-emails.types';

const dataFrom = <T>(response: SettingsApiEnvelope<T>, fallback: string): T => {
  if (!response || response.success === false || response.data == null) {
    throw new Error(response?.message || response?.errors?.join(', ') || fallback);
  }
  return response.data;
};

export const getSystemEmails = async (): Promise<SystemEmailSummary[]> => {
  const response =
    await campaignApiGet<SettingsApiEnvelope<SystemEmailSummary[]>>('/v1/system-emails');
  return dataFrom(response, 'Failed to load system emails');
};

export const getSystemEmail = async (key: string, locale?: string): Promise<SystemEmailDetail> => {
  const response = await campaignApiGet<SettingsApiEnvelope<SystemEmailDetail>>(
    `/v1/system-emails/${encodeURIComponent(key)}`,
    { locale }
  );
  return dataFrom(response, 'Failed to load system email');
};

const emailPath = (key: string, locale: string) =>
  `/v1/system-emails/${encodeURIComponent(key)}?locale=${encodeURIComponent(locale)}`;

export const updateSystemEmail = async (
  key: string,
  locale: string,
  input: UpdateSystemEmailInput
): Promise<SystemEmailDetail> => {
  const response = await campaignApiPatch<SettingsApiEnvelope<SystemEmailDetail>>(
    emailPath(key, locale),
    input
  );
  return dataFrom(response, 'Failed to save system email');
};

export const resetSystemEmail = async (key: string, locale: string): Promise<SystemEmailDetail> => {
  const response = await campaignApiPatch<SettingsApiEnvelope<SystemEmailDetail>>(
    emailPath(key, locale),
    { reset: true }
  );
  return dataFrom(response, 'Failed to reset system email');
};

export const previewSystemEmail = async (
  key: string,
  locale?: string,
  draft?: SystemEmailPreviewInput
): Promise<SystemEmailPreview> => {
  const query = locale ? `?locale=${encodeURIComponent(locale)}` : '';
  const response = await campaignApiPost<SettingsApiEnvelope<SystemEmailPreview>>(
    `/v1/system-emails/${encodeURIComponent(key)}/preview${query}`,
    draft ?? {}
  );
  return dataFrom(response, 'Failed to preview system email');
};

export const startFromTemplate = async (
  key: string,
  input: StartFromTemplateInput
): Promise<StartFromTemplateResponse> => {
  const response = await campaignApiPost<SettingsApiEnvelope<StartFromTemplateResponse>>(
    `/v1/system-emails/${encodeURIComponent(key)}/start-from-template`,
    input
  );
  return dataFrom(response, 'Failed to start from template');
};

